<template>
  <!-- Launcher: a draggable floating button. Hidden while the chat is docked. -->
  <div
    v-show="!docked"
    ref="launcherRef"
    class="fixed z-30"
    :style="launcherStyle"
    data-testid="agent-chat-launcher"
  >
    <ButtonBase
      shape="circular"
      size="xl"
      aria-label="Open BidBoost agent chat"
      :aria-expanded="open"
      :class="[
        'shadow-lg hover:shadow-xl !transition-all !duration-200 touch-none',
        isDragging ? 'cursor-grabbing' : 'cursor-grab',
      ]"
      @pointerdown="startDragging"
      @click="toggle"
    >
      <SparklesIcon class="h-6 w-6" aria-hidden="true" />
    </ButtonBase>
  </div>

  <!--
    The chat window is a single element so the panel (and its conversation
    state) survives switching between the docked pane and the popover when the
    viewport is resized across the slideover breakpoint. The panel mounts on
    first open, keeping its form fields out of pages where the chat is unused,
    and then stays mounted so a closed chat keeps its conversation.
  -->
  <Transition
    enter-active-class="transition duration-200 ease-out"
    enter-from-class="opacity-0 translate-y-1"
    enter-to-class="opacity-100 translate-y-0"
    leave-active-class="transition duration-150 ease-in"
    leave-from-class="opacity-100 translate-y-0"
    leave-to-class="opacity-0 translate-y-1"
  >
    <div
      v-show="open"
      data-testid="agent-chat-window"
      :data-mode="docked ? 'pane' : 'popover'"
      :class="[
        'fixed bg-white dark:bg-gray-800',
        docked
          ? 'right-0 bottom-0 top-16 z-10 border-l border-gray-200 dark:border-white/5'
          : 'z-30 rounded-lg shadow-lg ring-1 ring-black ring-opacity-5',
        isResizingPane && 'cursor-col-resize select-none',
      ]"
      :style="windowStyle"
    >
      <div
        class="relative h-full overflow-hidden"
        :class="[!docked && 'rounded-lg']"
      >
        <AgentChatPanel
          v-if="panelMounted"
          :open="open"
          :docked="docked"
          @close="close"
        />
      </div>
      <div
        v-if="docked"
        class="absolute inset-y-0 left-0 w-2 cursor-col-resize"
        aria-hidden="true"
        @mousedown.prevent="startResizingPane"
      ></div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import ButtonBase from '@/Common/Elements/Buttons/ButtonBase.vue'
import { useSidePaneStore } from '@/Common/Overlays/Slideovers/useSidePaneStore'
import { SparklesIcon } from '@heroicons/vue/20/solid'
import {
  StorageSerializers,
  useElementBounding,
  useEventListener,
  useLocalStorage,
  useWindowSize,
} from '@vueuse/core'
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import AgentChatPanel from './AgentChatPanel.vue'

type LauncherPosition = {
  x: number
  y: number
}

type SavedLauncherPosition = Partial<LauncherPosition> & {
  anchoredRight?: boolean
  anchoredBottom?: boolean
  right?: number
  bottom?: number
}

const storageKey = 'agent_chat_popover_position'
const defaultViewportMargin = 24
const minimumViewportMargin = 8
const dragThreshold = 4

// Popover geometry, matching the panel's own `h-[min(720px,calc(100vh-7rem))]`.
const popoverWidth = 384
const popoverMaxHeight = 720
const popoverViewportInset = 112
const popoverGap = 15
const popoverViewportMargin = 16

const {
  sidePaneOpen: open,
  sidePaneDocked: docked,
  sidePaneWidth,
  setSidePaneWidth,
} = useSidePaneStore()

const panelMounted = ref(open.value)
watch(open, (isOpen) => {
  if (isOpen) panelMounted.value = true
})

const launcherRef = ref<HTMLElement | null>(null)
const launcherRect = useElementBounding(launcherRef)
const { width: viewportWidth, height: viewportHeight } = useWindowSize()

const savedPosition = useLocalStorage<SavedLauncherPosition>(
  storageKey,
  {},
  {
    serializer: StorageSerializers.object,
  }
)
const position = ref<LauncherPosition | null>(null)
const anchoredRight = ref(false)
const anchoredBottom = ref(false)
const rightOffset = ref(defaultViewportMargin)
const bottomOffset = ref(defaultViewportMargin)
const isDragging = ref(false)
const clickShouldBeSuppressed = ref(false)

let cleanupPointerMove: (() => void) | null = null
let cleanupPointerUp: (() => void) | null = null
let dragStart: {
  pointerX: number
  pointerY: number
  positionX: number
  positionY: number
  anchoredRight: boolean
  anchoredBottom: boolean
  rightOffset: number
  bottomOffset: number
} | null = null

const launcherStyle = computed(() => {
  if (!position.value) {
    return {
      bottom: `${defaultViewportMargin}px`,
      right: `${defaultViewportMargin}px`,
    }
  }

  return {
    left: `${position.value.x}px`,
    top: `${position.value.y}px`,
  }
})

const windowStyle = computed(() => {
  if (docked.value) {
    return { width: `${sidePaneWidth.value}px` }
  }

  return popoverStyle.value
})

// Place the popover above the launcher, right edges aligned; fall back to
// below it, and otherwise keep it inside the viewport.
const popoverStyle = computed(() => {
  const width = Math.min(
    popoverWidth,
    viewportWidth.value - 2 * popoverViewportMargin
  )
  const height = Math.min(
    popoverMaxHeight,
    viewportHeight.value - popoverViewportInset
  )
  const launcher = launcherRect
  const launcherRight =
    launcher.right.value || viewportWidth.value - defaultViewportMargin
  const launcherTop =
    launcher.top.value || viewportHeight.value - defaultViewportMargin - 56
  const launcherBottom =
    launcher.bottom.value || viewportHeight.value - defaultViewportMargin

  const left = clamp(
    launcherRight - width,
    minimumViewportMargin,
    Math.max(
      minimumViewportMargin,
      viewportWidth.value - width - minimumViewportMargin
    )
  )

  const above = launcherTop - popoverGap - height
  const below = launcherBottom + popoverGap
  let top = above
  if (above < minimumViewportMargin) {
    top =
      below + height + minimumViewportMargin <= viewportHeight.value
        ? below
        : clamp(
            above,
            minimumViewportMargin,
            Math.max(
              minimumViewportMargin,
              viewportHeight.value - height - minimumViewportMargin
            )
          )
  }

  return {
    left: `${Math.round(left)}px`,
    top: `${Math.round(top)}px`,
    width: `${Math.round(width)}px`,
  }
})

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

onMounted(async () => {
  await nextTick()
  initializePosition()
})

useEventListener('resize', () => {
  if (!position.value) return

  updatePositionForViewportResize()
})

function toggle(event: MouseEvent) {
  if (clickShouldBeSuppressed.value) {
    event.preventDefault()
    clickShouldBeSuppressed.value = false
    return
  }

  open.value = !open.value
}

function close() {
  open.value = false
}

// Docked pane resizing (drag the pane's left edge).
const isResizingPane = ref(false)
let cleanupPaneMove: (() => void) | null = null
let cleanupPaneUp: (() => void) | null = null

function startResizingPane() {
  isResizingPane.value = true

  cleanupPaneMove = useEventListener(
    window,
    'mousemove',
    (event: MouseEvent) => {
      setSidePaneWidth(window.innerWidth - event.clientX)
    }
  )
  cleanupPaneUp = useEventListener(window, 'mouseup', stopResizingPane)
}

function stopResizingPane() {
  isResizingPane.value = false

  if (cleanupPaneMove) cleanupPaneMove()
  if (cleanupPaneUp) cleanupPaneUp()

  cleanupPaneMove = null
  cleanupPaneUp = null
}

function startDragging(event: PointerEvent) {
  if (event.pointerType === 'mouse' && event.button !== 0) return

  initializePosition()

  if (!position.value) return

  dragStart = {
    pointerX: event.clientX,
    pointerY: event.clientY,
    positionX: position.value.x,
    positionY: position.value.y,
    anchoredRight: anchoredRight.value,
    anchoredBottom: anchoredBottom.value,
    rightOffset: rightOffset.value,
    bottomOffset: bottomOffset.value,
  }

  cleanupPointerMove = useEventListener(window, 'pointermove', drag)
  cleanupPointerUp = useEventListener(window, 'pointerup', stopDragging)
}

function drag(event: PointerEvent) {
  if (!dragStart) return

  const deltaX = event.clientX - dragStart.pointerX
  const deltaY = event.clientY - dragStart.pointerY

  if (!isDragging.value && Math.hypot(deltaX, deltaY) < dragThreshold) {
    return
  }

  isDragging.value = true
  clickShouldBeSuppressed.value = true
  event.preventDefault()

  const nextPosition = {
    x: dragStart.positionX + deltaX,
    y: dragStart.positionY + deltaY,
  }
  const clampedPosition = clampPosition(nextPosition)
  const bounds = getPositionBounds()

  if (dragStart.anchoredRight && Math.abs(deltaX) < dragThreshold) {
    anchoredRight.value = true
    rightOffset.value = dragStart.rightOffset
    clampedPosition.x = clampPosition({
      x: getRightAnchoredX(rightOffset.value),
      y: clampedPosition.y,
    }).x
  } else {
    anchoredRight.value = nextPosition.x >= bounds.maxX
    rightOffset.value = getRightOffset(clampedPosition)
  }

  if (dragStart.anchoredBottom && Math.abs(deltaY) < dragThreshold) {
    anchoredBottom.value = true
    bottomOffset.value = dragStart.bottomOffset
    clampedPosition.y = clampPosition({
      x: clampedPosition.x,
      y: getBottomAnchoredY(bottomOffset.value),
    }).y
  } else {
    anchoredBottom.value = nextPosition.y >= bounds.maxY
    bottomOffset.value = getBottomOffset(clampedPosition)
  }

  position.value = clampedPosition
}

function stopDragging() {
  if (isDragging.value && position.value) {
    saveCurrentPosition()
  }

  dragStart = null
  isDragging.value = false

  if (cleanupPointerMove) cleanupPointerMove()
  if (cleanupPointerUp) cleanupPointerUp()

  cleanupPointerMove = null
  cleanupPointerUp = null

  window.setTimeout(() => {
    clickShouldBeSuppressed.value = false
  })
}

function initializePosition() {
  const saved = getSavedPosition()

  if (saved) {
    position.value = saved
    return
  }

  anchoredRight.value = true
  anchoredBottom.value = true
  rightOffset.value = defaultViewportMargin
  bottomOffset.value = defaultViewportMargin
  position.value = clampPosition(getDefaultPosition())
}

function updatePositionForViewportResize() {
  const nextPosition = getPositionForCurrentAnchors()
  const clampedPosition = clampPosition(nextPosition)
  const bounds = getPositionBounds()

  if (!anchoredRight.value && nextPosition.x >= bounds.maxX) {
    anchoredRight.value = true
    rightOffset.value = getRightOffset(clampedPosition)
  }

  if (!anchoredBottom.value && nextPosition.y >= bounds.maxY) {
    anchoredBottom.value = true
    bottomOffset.value = getBottomOffset(clampedPosition)
  }

  position.value = clampedPosition
  saveCurrentPosition()
}

function clampPosition(nextPosition: LauncherPosition): LauncherPosition {
  const { maxX, maxY } = getPositionBounds()

  return {
    x: Math.min(Math.max(nextPosition.x, minimumViewportMargin), maxX),
    y: Math.min(Math.max(nextPosition.y, minimumViewportMargin), maxY),
  }
}

function getPositionBounds() {
  const { width, height } = getLauncherDimensions()

  return {
    width,
    height,
    maxX: Math.max(
      minimumViewportMargin,
      window.innerWidth - width - minimumViewportMargin
    ),
    maxY: Math.max(
      minimumViewportMargin,
      window.innerHeight - height - minimumViewportMargin
    ),
  }
}

function getDefaultPosition(): LauncherPosition {
  const { width, height } = getLauncherDimensions()

  return {
    x: window.innerWidth - width - defaultViewportMargin,
    y: window.innerHeight - height - defaultViewportMargin,
  }
}

function getPositionForCurrentAnchors(): LauncherPosition {
  return {
    x: anchoredRight.value
      ? getRightAnchoredX(rightOffset.value)
      : (position.value?.x ?? getDefaultPosition().x),
    y: anchoredBottom.value
      ? getBottomAnchoredY(bottomOffset.value)
      : (position.value?.y ?? getDefaultPosition().y),
  }
}

function getRightAnchoredX(offset: number): number {
  const { width } = getLauncherDimensions()

  return window.innerWidth - width - offset
}

function getBottomAnchoredY(offset: number): number {
  const { height } = getLauncherDimensions()

  return window.innerHeight - height - offset
}

function getRightOffset(nextPosition: LauncherPosition): number {
  const { width } = getLauncherDimensions()

  return Math.max(
    minimumViewportMargin,
    window.innerWidth - width - nextPosition.x
  )
}

function getBottomOffset(nextPosition: LauncherPosition): number {
  const { height } = getLauncherDimensions()

  return Math.max(
    minimumViewportMargin,
    window.innerHeight - height - nextPosition.y
  )
}

function getLauncherDimensions() {
  const rect = launcherRef.value?.getBoundingClientRect()

  return {
    width: rect?.width || 56,
    height: rect?.height || 56,
  }
}

function getSavedPosition(): LauncherPosition | null {
  const {
    x,
    y,
    anchoredRight: savedAnchoredRight,
    anchoredBottom: savedAnchoredBottom,
    right,
    bottom,
  } = savedPosition.value ?? {}
  const { width, height } = getLauncherDimensions()
  const canAnchorRight =
    savedAnchoredRight === true && typeof right === 'number'
  const canAnchorBottom =
    savedAnchoredBottom === true && typeof bottom === 'number'

  if (typeof x !== 'number' && !canAnchorRight) {
    return null
  }

  if (typeof y !== 'number' && !canAnchorBottom) {
    return null
  }

  const nextPosition = clampPosition({
    x: canAnchorRight ? window.innerWidth - width - right : (x ?? 0),
    y: canAnchorBottom ? window.innerHeight - height - bottom : (y ?? 0),
  })
  const savedRightOffset = getRightOffset(nextPosition)
  const savedBottomOffset = getBottomOffset(nextPosition)

  anchoredRight.value =
    canAnchorRight || savedRightOffset <= defaultViewportMargin
  anchoredBottom.value =
    canAnchorBottom || savedBottomOffset <= defaultViewportMargin
  rightOffset.value = canAnchorRight ? right : savedRightOffset
  bottomOffset.value = canAnchorBottom ? bottom : savedBottomOffset

  return nextPosition
}

function saveCurrentPosition() {
  if (!position.value) return

  savedPosition.value = {
    ...position.value,
    anchoredRight: anchoredRight.value,
    anchoredBottom: anchoredBottom.value,
    right: rightOffset.value,
    bottom: bottomOffset.value,
  }
}

onUnmounted(() => {
  if (cleanupPointerMove) cleanupPointerMove()
  if (cleanupPointerUp) cleanupPointerUp()
  stopResizingPane()
})
</script>
